import * as Sentry from "@sentry/react";
import { logError } from "./errorLogging";

let handlersInstalled = false;

const getReasonType = (reason: unknown): string => {
  if (reason instanceof Error) return reason.name || "Error";
  if (reason === null) return "null";
  if (Array.isArray(reason)) return "array";
  return typeof reason;
};

const normalizeUnknownError = (value: unknown, fallbackMessage: string) => {
  if (value instanceof Error) return value;

  let message = fallbackMessage;
  if (typeof value === "string") {
    message = value;
  } else if (value !== undefined) {
    try {
      message = JSON.stringify(value);
    } catch {
      message = String(value);
    }
  }

  return new Error(message || fallbackMessage);
};

// Global promise rejection handler
export function setupGlobalErrorHandlers() {
  if (handlersInstalled || typeof window === "undefined") {
    return;
  }

  handlersInstalled = true;

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const normalizedError = normalizeUnknownError(
      event.reason,
      "Unhandled promise rejection"
    );
    const reasonType = getReasonType(event.reason);

    Sentry.addBreadcrumb({
      category: "global.unhandledrejection",
      level: "error",
      message: normalizedError.message,
      data: {
        route: window.location.pathname,
        reasonType,
      },
    });

    logError(
      "Unhandled promise rejection",
      "critical",
      {
        reason: event.reason,
        reasonType,
        route: window.location.pathname,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      normalizedError
    );
  });

  // Handle uncaught exceptions
  window.addEventListener("error", (event) => {
    const normalizedError = normalizeUnknownError(
      event.error ?? event.message,
      "Uncaught error"
    );

    Sentry.addBreadcrumb({
      category: "global.error",
      level: "error",
      message: event.message,
      data: {
        route: window.location.pathname,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });

    logError(
      "Uncaught error",
      "critical",
      {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: window.location.pathname,
        url: window.location.href,
      },
      normalizedError
    );
  });
}

// API timeout wrapper
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs = 10000,
  errorMessage = "Operation timed out"
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
}

// Retry with exponential backoff
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === maxRetries) {
        logError(
          `Operation failed after ${maxRetries + 1} attempts`,
          "high",
          { attempts: attempt + 1 },
          lastError
        );
        throw lastError;
      }

      // Exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}
